import { durableGet, durableSet } from "./binding";

/**
 * Persistent command idempotency (Phase 2 reliability fix).
 *
 * Durable commands are delivered at-least-once (live event + reconnect-drain +
 * server retry sweep), so the TV MUST execute each `commandId` only once. For
 * most command types re-running is merely wasteful, but for `reboot` it is
 * catastrophic: the reboot handler reloads the page, the reload reconnects and
 * drains the still-open command, and it reboots again - an infinite
 * black-screen loop. Persistence (not memory) is essential here precisely
 * because a reboot tears the page down: the executed-set has to survive the
 * reload that the command itself triggered.
 *
 * DURABLE, not localStorage (bug fixed 2026-07-21): this set has to survive
 * exactly the event that localStorage does not - a hard reboot on hotel-TV /
 * STB firmware that clean-states WebView storage. The binding lives in the
 * native store for that reason (see binding.ts), and an executed-set that is
 * *less* durable than the binding is worse than no dedupe at all: the TV comes
 * up still paired, drains a command the server has kept open, and re-runs it
 * with an empty guard. For `unpair` that means a paired TV wipes its own
 * binding on the first boot after an operator unpaired-and-re-paired it
 * (server keeps the command queued for 10 min, and it is keyed by a screenId
 * that survives re-pairing) - i.e. "the STB asks for a new pairing code after
 * every reboot". For `checkout_wipe` it means a wipe→reload loop.
 */

/** Exported so the checkout wipe can PRESERVE it: clearing the executed-set
 *  would let the wipe's own reload re-drain and re-run it forever. */
export const DEDUPE_KEY = "foyer.executedCommands";
const KEY = DEDUPE_KEY;
const MAX = 200;

function load(): string[] {
  try {
    const raw = durableGet(KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? (arr as string[]) : [];
  } catch {
    return [];
  }
}

/** Has this command already been executed on this device? */
export function wasExecuted(commandId: string): boolean {
  return load().includes(commandId);
}

/** Record a command as executed (bounded FIFO; survives reloads/reboots). */
export function markExecuted(commandId: string): void {
  try {
    const arr = load().filter((id) => id !== commandId);
    arr.push(commandId);
    while (arr.length > MAX) arr.shift();
    durableSet(KEY, JSON.stringify(arr));
  } catch {
    // Store unavailable (private mode / wiped) - degrade to no dedupe;
    // the awaited ack-before-reload is the second line of defence.
  }
}
